import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getInvoices,
  getInvoice,
  createInvoice,
  updateInvoice,
  sendInvoice,
  deleteInvoice,
  recordPayment,
  invoicePath,
  type Invoice,
  type InvoiceDetail,
  type InvoiceItem,
  type CreateInvoiceData,
  type UpdateInvoiceData,
  type RecordPaymentData,
} from '@/lib/api/invoices';
import {
  InvoiceStatusBadge,
  InvoiceDetailModal,
  InvoiceModal,
  RecordPaymentModal,
  ImportFromQuoteModal,
} from '@/components/invoices';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DrawBillingPanel } from './DrawBillingPanel';
import apiClient from '@/lib/api/client';

interface InvoicesTabProps {
  jobId: string;
  clientId: number;
}

export const InvoicesTab = forwardRef(function InvoicesTab({ jobId, clientId }: InvoicesTabProps, ref) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  // CR #172 — the job dropdown in the invoice form was empty: this tab never
  // passed a jobs list. Load the client's jobs (NB: the endpoint's filter is
  // camelCase clientId; snake_case is silently ignored — the #113 bug).
  const [clientJobs, setClientJobs] = useState<{ id: number; title: string }[]>([]);
  useEffect(() => {
    apiClient.get('/jobs', { params: { clientId, limit: 100 } })
      .then(r => {
        const d = r.data?.data ?? r.data;
        const rows = (Array.isArray(d) ? d : d?.jobs ?? []) as { id: number; title: string }[];
        setClientJobs(rows.map(j => ({ id: j.id, title: j.title })));
      })
      .catch(() => {});
  }, [clientId]);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [showImportQuoteModal, setShowImportQuoteModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<(Invoice & { items?: InvoiceItem[] }) | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { toasts, removeToast, toast } = useToast();
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    openCreateInvoice: () => {
      setShowCreateOptions(true);
    }
  }), []);

  useEffect(() => {
    loadInvoices();
  }, [jobId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInvoices({ job_id: Number(jobId), limit: 100 });
      setInvoices(data.invoices);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoiceId: number) => {
    try {
      const invoiceDetail = await getInvoice(invoiceId);
      setSelectedInvoice(invoiceDetail);
      setShowInvoiceDetailModal(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleEditInvoice = () => {
    if (selectedInvoice && selectedInvoice.status !== 'paid') {
      setEditingInvoice({
        ...selectedInvoice,
        items: selectedInvoice.items,
      });
      setShowInvoiceDetailModal(false);
      setShowInvoiceModal(true);
    }
  };

  const handleSaveInvoice = async (data: UpdateInvoiceData, invoiceId?: number) => {
    try {
      if (invoiceId) {
        const result = await updateInvoice(invoiceId, data as UpdateInvoiceData);
        if (result && result.id) {
          // A revision was created
          toast.success(`Revision ${result.invoiceNumber} created`);
          setShowInvoiceModal(false);
          setEditingInvoice(null);
          await loadInvoices();
          // Open the new revision detail
          handleViewInvoice(result.id);
          return;
        }
        toast.success('Invoice updated successfully');
      } else {
        // Create path — always link the new invoice to this job & client
        const result = await createInvoice({
          ...(data as CreateInvoiceData),
          client_id: clientId,
          job_id: Number(jobId),
        });
        toast.success(`Invoice ${result.invoiceNumber} created`);
      }
      setShowInvoiceModal(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      throw error;
    }
  };

  const handleViewRevision = async (invoiceId: number) => {
    try {
      const invoiceDetail = await getInvoice(invoiceId);
      setSelectedInvoice(invoiceDetail);
    } catch (error) {
      console.error('Failed to load revision:', error);
      toast.error('Failed to load revision');
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await sendInvoice(selectedInvoice.id);
      toast.success('Invoice marked as sent');
      const invoiceDetail = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(invoiceDetail);
      loadInvoices();
    } catch (error) {
      console.error('Failed to send invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  const handleRecordPayment = () => {
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (data: RecordPaymentData) => {
    if (!selectedInvoice) return;
    try {
      await recordPayment(selectedInvoice.id, data);
      toast.success('Payment recorded successfully');
      const invoiceDetail = await getInvoice(selectedInvoice.id);
      setSelectedInvoice(invoiceDetail);
      setShowPaymentModal(false);
      loadInvoices();
    } catch (error) {
      console.error('Failed to record payment:', error);
      throw error;
    }
  };

  const handleDeleteInvoice = () => {
    if (!selectedInvoice) return;
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    setConfirmDeleteOpen(false);
    try {
      await deleteInvoice(selectedInvoice.id);
      toast.success('Invoice deleted successfully');
      setShowInvoiceDetailModal(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-4">error</span>
          <p className="text-gray-900 font-semibold mb-2">Failed to load invoices</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadInvoices}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const draftInvoices = invoices.filter(i => i.status === 'draft');
  const sentInvoices = invoices.filter(i => i.status === 'sent' || i.status === 'viewed');
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const totalPaid = invoices.reduce((sum, inv) => sum + inv.amount_paid, 0);

  return (
    <div>
      {/* Progress / draw billing schedule (#78) */}
      <DrawBillingPanel jobId={jobId} onInvoiceCreated={loadInvoices} />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Invoices
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'} • Total: {formatCurrency(totalAmount)}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {invoices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-gray-600 text-sm">receipt_long</span>
              <p className="text-gray-600 text-sm">Total</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-gray-600 text-sm">edit</span>
              <p className="text-gray-600 text-sm">Draft</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{draftInvoices.length}</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-blue-500 text-sm">send</span>
              <p className="text-gray-600 text-sm">Sent</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{sentInvoices.length}</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-green-500 text-sm">check_circle</span>
              <p className="text-gray-600 text-sm">Paid</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{paidInvoices.length}</p>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <span className="material-symbols-outlined text-6xl text-gray-600 mb-4">
            receipt_long
          </span>
          <p className="text-gray-600 mb-4">
            No invoices created yet for this job
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="bg-white rounded-xl p-4 border border-gray-200 hover:border-primary transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-gray-900 font-bold text-lg">
                      {invoice.invoice_number}
                    </h3>
                    <InvoiceStatusBadge status={invoice.status} size="sm" />
                    {/* Client-portal visibility opt-in (#102) — hidden is the default;
                        Send also flips it on */}
                    <button
                      onClick={async () => {
                        const next = !invoice.visible_to_client;
                        try {
                          await apiClient.patch(`/invoices/${invoice.id}/visibility`, { visible_to_client: next });
                          setInvoices(prev => prev.map(i => i.id === invoice.id ? { ...i, visible_to_client: next ? 1 : 0 } : i));
                        } catch { toast.error('Failed to update visibility'); }
                      }}
                      title={invoice.visible_to_client
                        ? 'Client can see & pay this in their portal — click to hide'
                        : 'Hidden from the client portal — click to share'}
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                        invoice.visible_to_client
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-gray-100 text-gray-400 border-gray-200 line-through'
                      }`}
                    >
                      Client
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Issued {formatDate(invoice.invoice_date)} • Due {formatDate(invoice.due_date)}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 mb-3 border-t border-b border-gray-200">
                <div>
                  <span className="text-gray-600 text-sm font-medium">Total</span>
                  <span className="text-gray-900 font-bold text-xl ml-3">
                    {formatCurrency(invoice.total)}
                  </span>
                </div>
                {invoice.balance_due > 0 && invoice.balance_due < invoice.total && (
                  <div className="text-right">
                    <span className="text-gray-600 text-xs">Balance Due</span>
                    <p className="text-orange-600 font-bold">
                      {formatCurrency(invoice.balance_due)}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(invoicePath({ id: invoice.id, client_id: invoice.client_id ?? clientId, job_id: invoice.job_id ?? Number(jobId) }))}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">visibility</span>
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paid Summary Banner */}
      {paidInvoices.length > 0 && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="material-symbols-outlined text-green-600">check_circle</span>
                <h3 className="text-green-700 font-bold">Payment Received</h3>
              </div>
              <p className="text-gray-600 text-sm">
                {paidInvoices.length} {paidInvoices.length === 1 ? 'invoice' : 'invoices'} fully paid
              </p>
            </div>
            <div className="text-right">
              <p className="text-gray-600 text-xs mb-1">Total Paid</p>
              <p className="text-green-700 font-bold text-xl">
                {formatCurrency(totalPaid)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={showInvoiceDetailModal}
        onClose={() => {
          setShowInvoiceDetailModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onEdit={selectedInvoice?.status !== 'paid' ? handleEditInvoice : undefined}
        onSend={handleSendInvoice}
        onRecordPayment={handleRecordPayment}
        onDelete={handleDeleteInvoice}
        onViewRevision={handleViewRevision}
        onRefresh={(updated) => setSelectedInvoice(updated)}
      />

      {/* Invoice Edit Modal */}
      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
          setEditingInvoice(null);
        }}
        onSave={handleSaveInvoice}
        invoice={editingInvoice || undefined}
        clientId={selectedInvoice?.client_id || editingInvoice?.client_id || clientId}
        clientName={selectedInvoice?.client_name || editingInvoice?.client_name || ''}
        jobs={clientJobs}
        defaultJobId={Number(jobId)}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handleSubmitPayment}
        invoice={selectedInvoice}
      />

      {/* Create Invoice Options Modal */}
      {showCreateOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create Invoice</h3>
            <p className="text-sm text-gray-600 mb-6">How would you like to create this invoice?</p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowCreateOptions(false);
                  setEditingInvoice(null);
                  setShowInvoiceModal(true);
                }}
                className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-primary text-2xl">add_circle</span>
                <div>
                  <p className="font-semibold text-gray-900">Create Blank Invoice</p>
                  <p className="text-xs text-gray-500">Start from scratch</p>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowCreateOptions(false);
                  setShowImportQuoteModal(true);
                }}
                className="w-full flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-primary text-2xl">file_copy</span>
                <div>
                  <p className="font-semibold text-gray-900">Import from Quote</p>
                  <p className="text-xs text-gray-500">Auto-populate from estimate</p>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowCreateOptions(false)}
              className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Import from Quote Modal */}
      <ImportFromQuoteModal
        isOpen={showImportQuoteModal}
        jobId={jobId}
        clientId={clientId}
        onClose={() => setShowImportQuoteModal(false)}
        onSuccess={() => {
          loadInvoices();
          setShowImportQuoteModal(false);
        }}
      />

      {/* Delete Invoice Confirm */}
      {confirmDeleteOpen && (
        <ConfirmDialog
          title="Delete Invoice"
          message="Are you sure you want to delete this invoice? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteInvoice}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
});
